import React from 'react';
import { useTranslation } from 'react-i18next';
import { HandIcon, RadioIcon, ShieldCheckIcon, HeartIcon, LeafIcon, MailIcon } from 'lucide-react';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import './i18n';
export function App() {
  const {
    t
  } = useTranslation();
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleContactClick = () => {
    window.location.href = 'mailto:contact@pressura.xyz';
  };
  return <div className="w-full min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-id="element-0">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-slate-900/80 backdrop-blur-md border-b border-white/10" data-id="element-1">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-2">
          <div className="flex items-center gap-2" data-id="element-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center" data-id="element-4">
              <HandIcon className="w-6 h-6 text-white" data-id="element-5" />
            </div>
            <span className="text-xl font-bold text-white" data-id="element-6">Pressura</span>
          </div>
          <LanguageSwitcher data-id="element-7" />
        </div>
      </header>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden" style={{
      backgroundImage: 'url(https://uploadthingy.s3.us-west-1.amazonaws.com/mssy9aaADTCzMwR1uvnF6Y/image1.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }} data-id="element-8">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/90 to-slate-900/95" data-id="element-9"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10" data-id="element-10">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6" data-id="element-11">
            {t('hero.title')}
            <br data-id="element-12" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400" data-id="element-13">
              {t('hero.subtitle')}
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-14">
            {t('hero.description')}
          </p>
          <div className="flex gap-4 justify-center" data-id="element-15">
            <button onClick={() => scrollToSection('features')} className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all cursor-pointer" data-id="element-16">
              {t('hero.cta')}
            </button>
            <button onClick={() => scrollToSection('use-cases')} className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all cursor-pointer" data-id="element-17">
              {t('hero.learnMore')}
            </button>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white/5 backdrop-blur-sm" data-id="element-18">
        <div className="max-w-7xl mx-auto" data-id="element-19">
          <h2 className="text-4xl font-bold text-white text-center mb-16" data-id="element-20">
            {t('features.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-21">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/10 hover:border-blue-500/50 transition-all" data-id="element-22">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6" data-id="element-23">
                <HandIcon className="w-8 h-8 text-white" data-id="element-24" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-25">
                {t('features.pressure.title')}
              </h3>
              <p className="text-gray-300" data-id="element-26">
                {t('features.pressure.description')}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/10 hover:border-blue-500/50 transition-all" data-id="element-27">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6" data-id="element-28">
                <RadioIcon className="w-8 h-8 text-white" data-id="element-29" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-30">
                {t('features.remote.title')}
              </h3>
              <p className="text-gray-300" data-id="element-31">
                {t('features.remote.description')}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/10 hover:border-blue-500/50 transition-all" data-id="element-32">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6" data-id="element-33">
                <ShieldCheckIcon className="w-8 h-8 text-white" data-id="element-34" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-35">
                {t('features.safety.title')}
              </h3>
              <p className="text-gray-300" data-id="element-36">
                {t('features.safety.description')}
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 px-6 relative overflow-hidden" style={{
      backgroundImage: 'url(https://uploadthingy.s3.us-west-1.amazonaws.com/2BNqaZFpedjksjnm9EChbB/im2.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }} data-id="element-37">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-blue-900/85 to-slate-900/90" data-id="element-38"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-39">
          <h2 className="text-4xl font-bold text-white text-center mb-16" data-id="element-40">
            {t('useCases.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-41">
            <div className="bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-md rounded-2xl p-8 border border-white/10" data-id="element-42">
              <HeartIcon className="w-12 h-12 text-blue-400 mb-4" data-id="element-43" />
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-44">
                {t('useCases.elderly.title')}
              </h3>
              <p className="text-gray-300" data-id="element-45">
                {t('useCases.elderly.description')}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-600/20 backdrop-blur-md rounded-2xl p-8 border border-white/10" data-id="element-46">
              <LeafIcon className="w-12 h-12 text-green-400 mb-4" data-id="element-47" />
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-48">
                {t('useCases.plants.title')}
              </h3>
              <p className="text-gray-300" data-id="element-49">
                {t('useCases.plants.description')}
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 px-6 relative overflow-hidden bg-slate-900" style={{
      backgroundImage: 'url(https://uploadthingy.s3.us-west-1.amazonaws.com/5jMxrDMJab2d6nGqe5G8TS/im4_4.png)',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }} data-id="element-50">
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-51">
          <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-lg" data-id="element-52">
            {t('contact.title')}
          </h2>
          <p className="text-xl text-white mb-8 drop-shadow-lg" data-id="element-53">
            {t('contact.description')}
          </p>
          <button onClick={handleContactClick} className="px-10 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold text-lg hover:shadow-lg hover:shadow-blue-500/50 transition-all cursor-pointer" data-id="element-54">
            {t('contact.button')}
          </button>
        </div>
      </section>
      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10" data-id="element-55">
        <div className="max-w-7xl mx-auto text-center" data-id="element-56">
          <div className="flex items-center justify-center gap-2 mb-2" data-id="element-57">
            <MailIcon className="w-5 h-5 text-gray-400" data-id="element-58" />
            <a href="mailto:contact@pressura.xyz" className="text-gray-300 hover:text-blue-400 transition-colors" data-id="element-59">
              contact@pressura.xyz
            </a>
          </div>
          <p className="text-gray-400" data-id="element-60">
            © 2025 Pressura. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}